import React, { useEffect, useRef, useState } from "react";
import { FadeIn, SlideIn } from "./ui/animations";
import Typed from "typed.js";
import { useReducedMotion } from "framer-motion";
import HeroSlider from "./HeroSlider";

const phrases = [
  "Kinesiología",
  "Entrenamiento Personalizado",
  "Recuperación Deportiva",
];

const Hero = ({ onHeroReady }) => {

  const elMobile = useRef(null);
  const elDesktop = useRef(null);
  const typed = useRef(null);
  const prefersReduced = useReducedMotion();

  // ----------------------------------------------------
  // DETECTAR MOBILE
  // ----------------------------------------------------
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ----------------------------------------------------
  // TYPED
  // ----------------------------------------------------
  useEffect(() => {
    if (prefersReduced) return;

    const target = isMobile ? elMobile.current : elDesktop.current;
    if (!target) return;

    typed.current = new Typed(target, {
      strings: phrases,
      typeSpeed: 90,
      backSpeed: 70,
      backDelay: 1100,
      loop: true,
      cursorChar: "",
    });

    return () => typed.current?.destroy();
  }, [isMobile, prefersReduced]);


  // ======================================================
  // RENDER
  // ======================================================
  return (
    // 🔹 Cambio 1: bajamos la altura mínima (antes: min-h-screen)
    <section className="relative w-full min-h-[75vh] overflow-hidden text-[#00698F]">
      {/* Fondo */}
      <div
        aria-hidden="true"
        className="fixed inset-0 z-0"
        style={{ backgroundColor: "#E5E5E5" }}
      />

      {/* ======================================================
          MOBILE VIEW
      ====================================================== */}
      {isMobile ? (
        <div className="relative z-10 w-full max-w-md mx-auto px-6 py-14 text-center">
          <FadeIn>
            <p className="text-[#00698F] font-semibold text-sm mb-2">
              Soluciones integrales en salud y bienestar
            </p>
          </FadeIn>

          <FadeIn>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#00698F] leading-tight text-center">
              Kinesiología personalizada
              <br />
              para tu <span className="text-[#00698F]">bienestar</span>.
            </h1>
          </FadeIn>

          <FadeIn>
            <p className="mt-4 text-base font-semibold text-[#00698F]">
              Servicios especializados en
            </p>
          </FadeIn>

          <FadeIn>
            <p
              ref={elMobile}
              className="text-xl font-semibold text-[#00698F]min-h-[1.4em]"
            >
              {prefersReduced ? phrases[0] : ""}
            </p>
          </FadeIn>

          <FadeIn>
            <p className="mt-4 text-sm text-[#00698F]/80 leading-relaxed">
              Te acompañamos en cada etapa de tu rehabilitación con atención
              cercana y un enfoque basado en evidencia.
            </p>
          </FadeIn>

          <div className="mt-8">
            <SlideIn dir="up">
              <a
                href="/agenda"
                className="block w-full rounded-xl bg-[#06b6fa] text-white py-3 font-medium shadow-sm mb-3"
              >
                Agenda tu sesión
              </a>
            </SlideIn>

            <SlideIn dir="up" delay={0.05}>
              <a
                href="#como-funciona"
                className="block w-full rounded-xl border border-[#06b6fa] bg-white text-[#0b2052] py-3 font-medium shadow-sm"
              >
                Conoce cómo trabajamos
              </a>
            </SlideIn>
          </div>

          <div className="mt-10">
            <HeroSlider onReady={onHeroReady} />
          </div>
        </div>
      ) : (
        /* ======================================================
            DESKTOP VIEW
        ====================================================== */
        // 🔹 Cambio 2: un poco menos padding abajo (antes: py-20)
        <div className="relative z-10 max-w-6xl mx-auto min-h-[70vh] px-6 sm:px-10 md:px-16 flex flex-col justify-center pt-20 pb-10">
          <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16">
            {/* Texto */}
            <div className="w-full md:w-1/2 text-center md:text-left">
              <FadeIn>
                <p className="text-[#00698F] font-semibold text-base md:text-lg tracking-tight">
                  Soluciones integrales en salud y bienestar
                </p>
              </FadeIn>

              <FadeIn>
                <h1 className="mt-3 text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-[#00698F]">
                  Kinesiología personalizada
                  <br className="hidden sm:block" /> para tu{" "}
                  <span className="text-[#00698F]">bienestar</span>.
                </h1>
              </FadeIn>

              <div className="mt-5 space-y-2">
                <FadeIn>
                  <p className="text-lg sm:text-xl font-semibold text-[#00698F]">
                    Servicios especializados en
                  </p>
                </FadeIn>

                <FadeIn>
                  <p
                  ref={elDesktop}
                  className="h-[1.3em] text-xl sm:text-2xl md:text-3xl font-semibold text-[#00698F]"
                  >
                  {prefersReduced ? phrases[0] : ""}
                  </p>
                </FadeIn>
              </div>

              <FadeIn>
                <p className="mt-6 max-w-xl text-base md:text-lg text-[#00698F]/80 leading-relaxed mx-auto md:mx-0">
                  Te acompañamos en cada etapa de tu rehabilitación con atención
                  cercana, planes de tratamiento a medida y un enfoque basado en
                  evidencia para que recuperes tu movilidad y calidad de vida.
                </p>
              </FadeIn>

              {/* Botones */}
              <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 justify-center md:justify-start">
                <SlideIn dir="up">
                  <a
                    href="/agenda"
                    className="inline-flex items-center justify-center rounded-xl bg-[#06b6fa] text-white px-7 py-3.5 text-[17px] font-semibold shadow-md transition hover:shadow-xl hover:-translate-y-0.5"
                  >
                    Agenda tu sesión
                  </a>
                </SlideIn>

                <SlideIn dir="up" delay={0.05}>
                  <a
                    href="#como-funciona"
                    className="inline-flex items-center justify-center rounded-xl border border-[#06b6fa] bg-white text-[#0b2052] px-6 py-3 text-base font-medium shadow-sm transition hover:shadow-lg hover:-translate-y-0.5"
                  >
                    Conoce cómo trabajamos
                  </a>
                </SlideIn>
              </div>
            </div>

            {/* Slider */}
            <div className="w-full md:w-1/2">
              <SlideIn dir="up">
                <HeroSlider onReady={onHeroReady} />
              </SlideIn>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
